"use client";

import { useRef } from "react";
import { PagePayoutSchema } from "@/sanity/pages/PagePayoutTemplate";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import CryptoPillar from "@/components/global/ui/CryptoPillar";
import { CTAButton } from "@/shadcn/ui/button";
import { useScroll, useTransform } from "framer-motion";
import ChipSerif from "@/components/global/ui/ChipSerif";

import bg from "@/public/global/bg-art.jpg";
import statue from "@/public/assets/heroStatue.png";
import statueFutures from "@/public/assets/heroStatueFutures.png";
import { PageAssetsSchema } from "@/sanity/pages/PageAssetsTemplate";

export default function HeroSection({
  data,
  className,
}: {
  data: PageAssetsSchema;
  className?: string;
}) {
  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ["start end", "end start"],
  });

  const yEthereum = useTransform(scrollYProgress, [0, 1], ["-3%", "3%"]);
  const ySolana = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const yBitcoin = useTransform(scrollYProgress, [0, 1], ["5%", "-5%"]);

  return (
    <section
      id="hero-section"
      ref={parallaxRef}
      className={cn(
        "relative overflow-clip px-[2rem] lg:px-[5rem] pt-[2rem] lg:pt-[3rem]",
        className,
      )}
    >
      <div className="relative overflow-clip w-[min(229.25rem,100%)] mx-auto rounded-[2rem] lg:rounded-[5rem] pt-[14rem] lg:py-[25rem] pb-0 min-h-[72.875rem] flex flex-col items-center">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#F6F9F7] to-[#E6F2EB]">
          <Image
            className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,90rem)] !max-w-none !h-full object-cover object-top mix-blend-luminosity opacity-5 lg:opacity-[.16]"
            src={bg}
            quality={100}
            alt="background art"
          />
        </div>

        <div className="container lg:px-[10rem] relative z-10 flex flex-col items-center lg:items-start">
          <div className="w-[min(93.5rem,100%)] flex flex-col items-start">
            <ChipSerif text={data?.heroChip} />

            {data?.heroText && (
              <div
                className="richtext richtext-start [&_span]:text-[#103037] [&_span]:font-instrument-sans [&_span]:font-semibold lg:[&_span]:font-bold lg:[&_span]:text-[#6DA991] lg:[&_span]:font-gestura
                prose-p:leading-[150%] prose-p:text-[2.25rem] lg:prose-p:text-[2rem] prose-p:w-[min(60.75rem,100%)] lg:[text-wrap-style:balance] prose-h1:pr-[1rem] lg:prose-h1:pr-0"
              >
                <PortableText
                  value={data.heroText}
                  components={richTextComponent}
                />
              </div>
            )}

            {data?.heroCTA && (
              <CTAButton className="mt-[3rem]" data={data.heroCTA} />
            )}
          </div>
        </div>

        {/*<CryptoPillar
          className="left-[calc(50%-21rem)] lg:left-[calc(50%+17rem)] h-[25rem] lg:!scale-100"
          type="ethereum"
          style={{ y: yEthereum }}
        />
        <CryptoPillar
          className="bottom-[5rem] lg:bottom-0 left-[calc(50%-9rem)] lg:left-[calc(50%+36rem)] h-[40rem] lg:h-[52rem] lg:!scale-[1.16]"
          type="solana"
          style={{ y: ySolana }}
        />
        <CryptoPillar
          className="bottom-[11rem] lg:bottom-0 left-[calc(50%+18rem)] lg:left-[calc(50%+77rem)] h-[17rem] lg:h-[30rem] lg:!scale-[1.08]"
          type="bitcoin"
          style={{ y: yBitcoin }}
        />*/}

        <div
          className={cn(
            "w-[62rem] lg:w-[min(120.75rem,75%)] bottom-0 lg:bottom-0 lg:right-0 -translate-x-[4rem] lg:translate-x-0 relative lg:absolute z-10 mt-[4.5rem] lg:mt-0",
            data.pageStyle === "crypto"
              ? "-translate-x-[4rem]"
              : "-translate-x-[6rem]",
          )}
        >
          <Image
            className="!w-full"
            src={data.pageStyle === "crypto" ? statue : statueFutures}
            quality={100}
            alt="statue"
          />
        </div>
      </div>
    </section>
  );
}
